export interface PendingReportsQuery {
  report_name?: string;
  organization_name?: string;
  project_name?: string;
  status?: string;
  time_range?: string;
}

import { Injectable } from '@angular/core';
import { RestfulEndpoint } from '../../services/restful-endpoint.service';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  PendingReportModel,
  PendingReportsDetailsResponseModel,
  PendingReportsResponseModel,
} from './pending-reports-models';

interface PendingReportsRequestParams extends PendingReportsQuery {
  page: number;
  per_page: number;
}

@Injectable({
  providedIn: 'root',
})
export class PendingReportsService {
  page: number = 1;
  per_page: number = 20;
  selectedReport: PendingReportModel;
  adjustedFloor = new BehaviorSubject<any>(null);

  // Creates a new instance of the pending reports service.
  constructor(private endPoint: RestfulEndpoint) {}

  // Requests the pending reports list with optional filters.
  async fetchPendingReportsList(filters?: PendingReportsQuery): Promise<PendingReportsResponseModel> {
    const params: PendingReportsRequestParams = {
      page: this.page,
      per_page: this.per_page,
      ...this.sanitizeFilters(filters),
    };
    return new Promise((resolve, reject) => {
      this.api_getPendingReportsList(params).subscribe(
        (res: PendingReportsResponseModel) => {
          resolve(res);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  // Requests the details for a specific pending report.
  async fetchPendingReportsDetails(report_id: string): Promise<PendingReportsDetailsResponseModel> {
    return new Promise((resolve, reject) => {
      this.api_getPendingReportsDetails(report_id).subscribe(
        (res: PendingReportsDetailsResponseModel) => {
          resolve(res);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  // Publishes a pending report after floors are reviewed.
  async publishPendingReport(reportId: string, reportFloors: string[]): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api_publishReport(reportId, reportFloors).subscribe(
        (res) => {
          resolve(res);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  // Updates the pending report with adjusted floors payload.
  async updatePendingReport(report_id: string, data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api_updateReportPath(report_id, data).subscribe(
        (res) => {
          resolve(res);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  // Rejects a pending report with the provided floors.
  async rejectPendingReport(report_id: string, reportFloors: string[]): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api_rejectPendingReport(report_id, reportFloors).subscribe(
        (res) => {
          resolve(res);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  // Retrieves pending reports list from the REST endpoint.
  private api_getPendingReportsList(params: PendingReportsRequestParams): Observable<any> {
    return this.endPoint.get('pending-video-reports', params);
  }

  // Retrieves pending report details from the REST endpoint.
  private api_getPendingReportsDetails(report_id: string): Observable<any> {
    return this.endPoint.get(report_id, null, 'pending-video-reports');
  }

  // Calls the publish endpoint for a pending report.
  private api_publishReport(report_id: string, reportFloors: string[]): Observable<any> {
    return this.endPoint.post(
      `${report_id}/publish`,
      { floors: reportFloors },
      null,
      'pending-video-reports',
    );
  }

  // Calls the update endpoint for a pending report.
  private api_updateReportPath(report_id: string, data: any): Observable<any> {
    return this.endPoint.post(report_id, data, null, 'pending-video-reports');
  }

  // Calls the reject endpoint for a pending report.
  private api_rejectPendingReport(report_id: string, reportFloors: string[]): Observable<any> {
    return this.endPoint.post(
      `${report_id}/reject`,
      { floors: reportFloors },
      null,
      'pending-video-reports',
    );
  }

  // Removes empty values from filter objects before sending them to the API.
  private sanitizeFilters(filters?: PendingReportsQuery): PendingReportsQuery {
    if (!filters) {
      return {};
    }
    return Object.entries(filters).reduce((cleaned, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        cleaned[key as keyof PendingReportsQuery] = value;
      }
      return cleaned;
    }, {} as PendingReportsQuery);
  }
}
